import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { fmtBDT, statusColor } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { ShieldAlert, Wallet, ArrowUpRight, Package, ShoppingCart, Sparkles, Zap, Search } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import DashboardSearchBar from "../components/DashboardSearchBar";

const KycBanner = ({ status, onSubmit }) => {
  if (status === "approved") return null;
  const map = {
    not_submitted: { tone: "border-amber-300 bg-amber-50", text: "Complete your KYC to unlock ordering and credit." },
    pending: { tone: "border-blue-300 bg-blue-50", text: "Your KYC is under review. We'll notify you once approved." },
    rejected: { tone: "border-red-300 bg-red-50", text: "Your KYC was rejected. Please update your documents and resubmit." },
  };
  const m = map[status] || map.not_submitted;
  return (
    <div data-testid="kyc-banner" className={`border ${m.tone} rounded-sm p-4 flex items-start gap-3`}>
      <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5" />
      <div className="flex-1">
        <div className="font-semibold text-sm">{m.text}</div>
        <Link to="/profile" data-testid="kyc-banner-cta" className="text-sm text-[#E11D48] font-semibold hover:underline">
          Go to Profile & KYC →
        </Link>
      </div>
    </div>
  );
};

const StatBlock = ({ label, value, sub }) => (
  <div className="industrial-card p-4 sm:p-5">
    <div className="overline">{label}</div>
    <div className="font-display text-2xl sm:text-3xl mt-2 break-words">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const QuickAddBySku = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/products", { params: { q } });
        setResults(data.slice(0, 5));
      } finally { setSearching(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const quickAdd = (p) => {
    add(p, p.moq || 1);
    toast.success(`+${p.moq || 1} × ${p.name}`);
    setQ(""); setResults([]);
  };

  return (
    <div className="industrial-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-[#E11D48]" />
        <div className="overline">Quick Add by SKU or Name</div>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="quick-add-input"
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Type SKU like JA-BRK-001 or 'brake'…"
          className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#E11D48]"
        />
      </div>
      {results.length > 0 && (
        <div className="mt-2 border border-slate-200 rounded-sm overflow-hidden divide-y divide-slate-100">
          {results.map((p) => (
            <button
              key={p.product_id} onClick={() => quickAdd(p)}
              data-testid={`quick-add-${p.sku}`}
              className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left transition-colors duration-150"
            >
              <div className="w-10 h-10 bg-slate-100 border border-slate-200 overflow-hidden shrink-0">
                {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-xs text-slate-500 font-mono">{p.sku} · {p.category}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold">{fmtBDT(p.price_bdt)}</div>
                <div className="text-xs text-[#E11D48]">+{p.moq || 1}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {q.length >= 2 && results.length === 0 && !searching && (
        <div className="mt-2 text-xs text-slate-500 italic">No matches. Try a different keyword.</div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [ws, setWs] = useState(null);
  const [orders, setOrders] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [w, o, f] = await Promise.all([
          api.get("/workshop/me"),
          api.get("/orders"),
          api.get("/featured-kit").catch(() => ({ data: null })),
        ]);
        setWs(w.data.workshop);
        setOrders(o.data || []);
        setFeatured(f.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Layout><div className="overline">Loading…</div></Layout>;
  const available = (ws?.credit_limit || 0) - (ws?.credit_used || 0);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Welcome back</div>
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl mt-1 leading-tight break-words">{user?.name?.split(" ")[0] || "Workshop"} — your bay floor</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/products" data-testid="dashboard-shop-button"
              className="inline-flex items-center gap-2 bg-[#E11D48] hover:bg-[#BE123C] text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200">
              <Package className="w-4 h-4" /> Browse Catalogue
            </Link>
            <Link to="/cart" data-testid="dashboard-cart-button"
              className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-900 text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200">
              <ShoppingCart className="w-4 h-4" /> Cart
            </Link>
          </div>
        </div>

        {/* Bay-floor search — sits between greeting and KYC alert per UX spec */}
        <DashboardSearchBar />

        <KycBanner status={ws?.kyc_status || "not_submitted"} />

        {/* Featured Kit of the Month */}
        {featured && ws?.kyc_status === "approved" && (
          <Link to="/kits" data-testid="featured-kit-banner" className="block group">
            <div className="relative overflow-hidden border border-slate-200 rounded-sm bg-slate-950 text-white">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-5 relative min-h-[220px] overflow-hidden">
                  <img src={featured.image_url} alt={featured.name} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/40 to-transparent" />
                </div>
                <div className="lg:col-span-7 p-6 lg:p-8 flex flex-col justify-center">
                  <span className="inline-flex items-center gap-2 self-start text-[10px] font-semibold uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm bg-[#E11D48] text-white w-fit">
                    <Sparkles className="w-3.5 h-3.5" /> {featured.featured_label || "Featured Kit"}
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mt-3 leading-none">{featured.name}</h3>
                  <p className="text-sm text-slate-300 mt-2 max-w-md">{featured.description}</p>

                  <div className="mt-5 flex items-end gap-6">
                    <div>
                      <div className="overline" style={{color: "#94a3b8"}}>Workshop Price</div>
                      <div className="font-display text-3xl text-[#E11D48] mt-1">{fmtBDT(featured.workshop_price_bdt)}</div>
                    </div>
                    <div className="pb-1">
                      <div className="overline" style={{color: "#94a3b8"}}>Was</div>
                      <div className="text-sm line-through text-slate-400 mt-1">{fmtBDT(featured.price_bdt)}</div>
                    </div>
                    {featured.featured_discount_pct > 0 && (
                      <div className="pb-1">
                        <span className="text-xs font-semibold bg-emerald-500 text-white px-2 py-1 rounded-sm">
                          Save {(featured.featured_discount_pct * 100).toFixed(0)}%
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-[#E11D48] transition-colors">
                    View kit details <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Credit summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="industrial-card p-5 lg:col-span-2 bg-slate-900 text-white border-slate-900">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline" style={{color: "#94a3b8"}}>Credit Status</div>
                <div className="font-display text-2xl sm:text-3xl lg:text-4xl mt-2 break-words" data-testid="credit-available">{fmtBDT(available)}</div>
                <div className="text-sm text-slate-300 mt-1">Available credit</div>
              </div>
              <Wallet className="w-8 h-8 text-[#E11D48]" />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="overline" style={{color: "#94a3b8"}}>Limit</div>
                <div className="font-semibold mt-1">{fmtBDT(ws?.credit_limit || 0)}</div>
              </div>
              <div>
                <div className="overline" style={{color: "#94a3b8"}}>Used</div>
                <div className="font-semibold mt-1">{fmtBDT(ws?.credit_used || 0)}</div>
              </div>
            </div>
            <div className="mt-4 h-2 bg-slate-700 overflow-hidden rounded-sm">
              <div className="h-full bg-[#E11D48]" style={{ width: ws?.credit_limit ? `${Math.min(100, (ws.credit_used / ws.credit_limit) * 100)}%` : "0%" }} />
            </div>
          </div>
          <StatBlock label="Total Orders" value={orders.length} sub="lifetime" />
          <StatBlock label="In Progress" value={orders.filter(o => !["delivered","cancelled"].includes(o.status)).length} sub="active orders" />
        </div>

        {/* Joy ID — every workshop & dealer gets a stable 4-digit platform ID */}
        {(user?.joy_id || user?.user_id) && (
          <div className="industrial-card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="account-id-card">
            <div className="min-w-0">
              <div className="overline">Your Joy ID</div>
              <div className="font-mono text-2xl sm:text-3xl text-slate-900 dark:text-white mt-2 tracking-tight" data-testid="account-id-value">
                {user.joy_id || user.user_id}
              </div>
              <div className="text-xs text-slate-500 mt-1.5">
                Your unique platform identity. Quote it when contacting support — every order, KYC document, credit history, saved VIN and Health Passport on JOY Automart is tied to it.
              </div>
            </div>
            <button
              data-testid="account-id-copy"
              onClick={() => { navigator.clipboard?.writeText(user.joy_id || user.user_id); toast.success("Joy ID copied"); }}
              className="self-start sm:self-auto shrink-0 inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-900 dark:text-white dark:border-white/20 dark:hover:border-white text-xs font-semibold px-4 py-2 rounded-sm"
            >
              Copy ID
            </button>
          </div>
        )}

        {/* Quick Add by SKU */}
        {ws?.kyc_status === "approved" && <QuickAddBySku />}

        {/* Recent orders */}
        <div className="industrial-card">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="overline">Recent Orders</div>
              <div className="font-display text-xl mt-1">Last 5 placed</div>
            </div>
            <Link to="/orders" className="text-sm font-semibold text-[#E11D48] hover:underline flex items-center gap-1">
              View all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          {orders.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No orders yet. Browse the catalog to place your first.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-5 py-3 overline">Order ID</th>
                  <th className="text-left px-5 py-3 overline">Items</th>
                  <th className="text-left px-5 py-3 overline">Total</th>
                  <th className="text-left px-5 py-3 overline">Status</th>
                  <th className="text-left px-5 py-3 overline">Payment</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 5).map((o) => (
                  <tr key={o.order_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-3"><Link to={`/orders/${o.order_id}`} className="font-mono text-xs hover:text-[#E11D48]">{o.order_id}</Link></td>
                    <td className="px-5 py-3">{o.items.length} items</td>
                    <td className="px-5 py-3 font-semibold">{fmtBDT(o.total_bdt)}</td>
                    <td className="px-5 py-3"><span className={`text-xs px-2 py-0.5 border rounded-sm ${statusColor(o.status)}`}>{o.status}</span></td>
                    <td className="px-5 py-3 text-xs uppercase">{o.payment_method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
